import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, AlertCircle } from 'lucide-react';
import { AnchorsGrid } from './AnchorsGrid';
import { Testimonials } from './Testimonials';
import { LegalModal, PrivacyContent, TermsContent } from './LegalModal';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.8,
  delay,
  ease: [0.22, 1, 0.36, 1]
}} className={className} data-id="element-6">
    {children}
  </motion.div>;
const Button = ({
  children,
  className = '',
  onClick
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => <motion.button whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} onClick={onClick} className={`bg-terracotta hover:bg-terracottaHover text-white font-medium py-4 px-8 rounded-sm transition-colors duration-300 flex items-center justify-center gap-2 w-full md:w-auto ${className}`} data-id="element-7">
    {children}
  </motion.button>;
export function LandingPage() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  const handleCheckout = () => {
    window.open('http://www.varenli.com/cart/42650957938782:1', '_blank');
  };
  const scrollToCTA = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-cream text-charcoal selection:bg-terracotta selection:text-white overflow-x-hidden" data-id="element-8">
      {/* 1. HERO SECTION */}
      <section className="relative min-h-[90vh] flex flex-col md:flex-row items-center" data-id="element-9">
        <div className="w-full md:w-1/2 h-[50vh] md:h-screen relative order-1 md:order-2" data-id="element-10">
          <div className="absolute inset-0 bg-charcoal/20 mix-blend-multiply z-10" data-id="element-11"></div>
          <img src="https://cdn.magicpatterns.com/uploads/vbReKsjJLvZQFiEJR6pFTc/una_mujer_landingpage.png" alt="Mujer mirando por la ventana" className="w-full h-full object-cover object-center grayscale" data-id="element-12" />
        </div>

        <div className="w-full md:w-1/2 px-6 py-16 md:p-16 lg:p-24 flex flex-col justify-center order-2 md:order-1 z-20 bg-cream md:bg-transparent" data-id="element-13">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} className="max-w-xl" data-id="element-14">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif leading-[1.1] mb-8 text-charcoal" data-id="element-15">
              Tu cuerpo <br data-id="element-16" />
              <span className="italic text-terracotta" data-id="element-17">no se equivocó</span>
            </h1>
            <p className="text-xl md:text-2xl font-light leading-relaxed mb-6 text-charcoal/80" data-id="element-18">
              Si estás teniendo bochornos, insomnio o calor repentino… no es tu
              edad.
            </p>
            <p className="text-lg mb-10 font-medium text-charcoal/90" data-id="element-19">
              Es un sistema que perdió el equilibrio — y nadie te explicó cómo
              recuperarlo.
            </p>
            <Button onClick={scrollToCTA} className="text-lg" data-id="element-20">
              👉 Quiero entender lo que me está pasando
            </Button>
          </motion.div>
        </div>
      </section>

      {/* 2. SYMPTOM RECOGNITION */}
      <section className="py-24 px-6 max-w-4xl mx-auto" data-id="element-21">
        <FadeIn data-id="element-22">
          <h2 className="text-3xl md:text-4xl font-serif mb-12 text-center" data-id="element-23">
            Si esto te está pasando… lo vas a reconocer:
          </h2>

          <div className="space-y-6 mb-16 pl-6 border-l-2 border-sage/40" data-id="element-24">
            {['Te despiertas en la madrugada con calor sin razón', 'Hay momentos donde sientes que tu cuerpo "se activa solo"', 'Duermes peor… y el día se vuelve más pesado', 'Te frustras porque antes podías manejar todo mejor'].map((item, i) => <p key={i} className="text-lg md:text-xl font-light text-charcoal/80 flex items-start" data-id="element-25">
                <span className="text-terracotta mr-3 mt-1" data-id="element-26">•</span> {item}
              </p>)}
          </div>

          <div className="text-center max-w-2xl mx-auto" data-id="element-27">
            <p className="text-xl mb-4 font-light" data-id="element-28">
              Y lo más difícil no es el síntoma.
            </p>
            <p className="text-xl mb-6 font-light" data-id="element-29">Es esto:</p>
            <p className="text-2xl md:text-3xl font-serif italic text-terracotta" data-id="element-30">
              Sentir que perdiste el control de tu propio cuerpo.
            </p>
          </div>
        </FadeIn>
      </section>

      {/* 3. TRUTH BOMBS & REFRAME */}
      <section className="py-24 px-6 bg-sand/40" data-id="element-31">
        <div className="max-w-3xl mx-auto" data-id="element-32">
          <FadeIn data-id="element-33">
            <h2 className="text-3xl font-serif mb-8 text-center" data-id="element-34">
              Lo que casi nadie te dice:
            </h2>
            <div className="bg-white p-8 md:p-10 rounded-sm shadow-sm mb-16" data-id="element-35">
              <p className="text-xl font-medium mb-4 flex items-start gap-3" data-id="element-36">
                <span className="text-terracotta" data-id="element-37">👉</span> Esto no es algo que
                simplemente desaparece solo
              </p>
              <p className="text-xl font-medium flex items-start gap-3" data-id="element-38">
                <span className="text-terracotta" data-id="element-39">👉</span> Y mientras no
                entiendas lo que está pasando… se vuelve más impredecible
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-40">
            <p className="text-xl text-center mb-12 font-light" data-id="element-41">
              No es tu culpa. Pero tampoco se va a resolver ignorándolo.
            </p>

            <div className="border-t border-b border-charcoal/10 py-12 text-center" data-id="element-42">
              <p className="text-2xl font-serif mb-8" data-id="element-43">
                El error no es lo que sientes. <br data-id="element-44" />
                <span className="italic text-terracotta" data-id="element-45">
                  El error es cómo te enseñaron a verlo.
                </span>
              </p>

              <p className="text-lg font-light mb-6" data-id="element-46">Te hicieron creer que:</p>
              <ul className="inline-block text-left space-y-3 mb-8" data-id="element-47">
                <li className="text-lg text-charcoal/80" data-id="element-48">
                  • Son síntomas aislados
                </li>
                <li className="text-lg text-charcoal/80" data-id="element-49">
                  • Que hay que "aguantar"
                </li>
                <li className="text-lg text-charcoal/80" data-id="element-50">
                  • Que es parte de la edad
                </li>
              </ul>

              <p className="text-xl font-medium mt-4" data-id="element-51">Pero en realidad…</p>
              <p className="text-2xl font-serif mt-4 text-sage" data-id="element-52">
                Tu cuerpo está reaccionando a señales internas desordenadas.
              </p>
              <p className="text-xl font-light mt-4" data-id="element-53">
                Y cuando esas señales cambian… todo empieza a tener sentido.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 4. THE 6 ANCHORS SYSTEM */}
      <section className="py-24 px-6 max-w-6xl mx-auto" data-id="element-54">
        <FadeIn data-id="element-55">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-56">
            <h2 className="text-sm uppercase tracking-widest text-terracotta font-semibold mb-4" data-id="element-57">
              Aquí es donde todo cambia
            </h2>
            <p className="text-2xl font-light mb-8" data-id="element-58">
              No necesitas más consejos sueltos. Necesitas entender el sistema
              completo.
            </p>
            <h3 className="text-4xl md:text-5xl font-serif mb-6" data-id="element-59">
              El sistema de las 6 anclas
            </h3>
            <p className="text-lg text-charcoal/80 font-light" data-id="element-60">
              No es un método. Es una forma de entender por qué tu cuerpo
              reacciona como reacciona… y cómo empezar a estabilizarlo desde
              dentro.
            </p>
          </div>
        </FadeIn>

        <AnchorsGrid data-id="element-61" />

        <FadeIn delay={0.3} className="mt-16 text-center" data-id="element-62">
          <p className="text-xl font-medium inline-flex items-center gap-2 bg-sand/50 px-6 py-3 rounded-full" data-id="element-63">
            <span className="text-terracotta" data-id="element-64">👉</span> No necesitas dominar
            todo hoy.
          </p>
          <p className="text-2xl font-serif italic mt-8 text-charcoal/90" data-id="element-65">
            Pero cuando entiendes esto… tu cuerpo deja de sentirse como un
            enemigo.
          </p>
        </FadeIn>
      </section>

      {/* 5. TRANSFORMATION */}
      <section className="py-24 px-6 bg-charcoal text-cream" data-id="element-66">
        <div className="max-w-3xl mx-auto text-center" data-id="element-67">
          <FadeIn data-id="element-68">
            <h2 className="text-3xl md:text-4xl font-serif mb-12" data-id="element-69">
              Algo cambia cuando entiendes lo que te está pasando.
            </h2>
            <p className="text-xl font-light mb-10 text-cream/80" data-id="element-70">
              No porque todo desaparezca de inmediato… Sino porque:
            </p>

            <div className="space-y-6 text-left max-w-md mx-auto" data-id="element-71">
              {['Dejas de asustarte', 'Empiezas a reconocer las señales', 'Sabes qué hacer en el momento'].map((item, i) => <div key={i} className="flex items-center gap-4" data-id="element-72">
                  <div className="bg-terracotta/20 p-2 rounded-full" data-id="element-73">
                    <Check className="w-5 h-5 text-terracotta" data-id="element-74" />
                  </div>
                  <p className="text-xl font-light" data-id="element-75">{item}</p>
                </div>)}
            </div>

            <p className="text-2xl font-serif italic mt-12 text-sage" data-id="element-76">
              Y eso… cambia completamente la experiencia.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 6. PRODUCT DETAILS */}
      <section className="py-24 px-6 max-w-4xl mx-auto" data-id="element-77">
        <FadeIn data-id="element-78">
          <div className="text-center mb-16" data-id="element-79">
            <h2 className="text-3xl md:text-4xl font-serif mb-6" data-id="element-80">
              Esto no es un ebook más.
            </h2>
            <p className="text-xl font-light text-charcoal/80" data-id="element-81">
              Es una guía diseñada para que entiendas tu cuerpo de una forma que
              nadie te explicó antes.
            </p>
          </div>

          <div className="bg-sand/30 border border-sand p-8 md:p-12 rounded-sm relative overflow-hidden" data-id="element-82">
            <div className="absolute top-0 right-0 w-64 h-64 bg-sage/10 rounded-full blur-3xl -mr-20 -mt-20" data-id="element-83"></div>

            <h3 className="text-2xl font-serif mb-8 relative z-10" data-id="element-84">
              Dentro de "Tu cuerpo no se equivocó" vas a encontrar:
            </h3>

            <ul className="space-y-5 mb-10 relative z-10" data-id="element-85">
              {['El sistema completo explicado paso a paso', 'Cómo identificar tus detonantes reales', 'Qué hacer en momentos específicos (mañana, noche, episodios)', 'Cómo empezar con una sola ancla sin sentirte abrumada'].map((item, i) => <li key={i} className="flex items-start gap-3 text-lg text-charcoal/80" data-id="element-86">
                  <span className="text-terracotta mt-1" data-id="element-87">✦</span>
                  <span data-id="element-88">{item}</span>
                </li>)}
            </ul>

            <div className="pt-8 border-t border-charcoal/10 relative z-10 flex flex-col sm:flex-row gap-6 justify-center" data-id="element-89">
              <p className="font-medium flex items-center gap-2" data-id="element-90">
                <span className="text-terracotta" data-id="element-91">👉</span> No es teoría.
              </p>
              <p className="font-medium flex items-center gap-2" data-id="element-92">
                <span className="text-terracotta" data-id="element-93">👉</span> Es comprensión
                aplicada a tu día a día.
              </p>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* 7. RESULTS PREVIEW & 8. TESTIMONIALS */}
      <section className="py-24 px-6 bg-cream border-t border-sand" data-id="element-94">
        <div className="max-w-5xl mx-auto" data-id="element-95">
          <FadeIn data-id="element-96">
            <div className="text-center max-w-2xl mx-auto mb-16" data-id="element-97">
              <p className="text-xl font-light mb-6" data-id="element-98">Esto no es inmediato.</p>
              <h2 className="text-3xl font-serif mb-8" data-id="element-99">
                Pero muchas mujeres empiezan a notar algo distinto en pocos
                días:
              </h2>

              <div className="flex flex-col sm:flex-row justify-center gap-6 sm:gap-12 mb-10" data-id="element-100">
                <div className="flex items-center gap-2 justify-center" data-id="element-101">
                  <span className="w-1.5 h-1.5 rounded-full bg-sage" data-id="element-102"></span>
                  <p className="text-lg" data-id="element-103">Momentos más tranquilos</p>
                </div>
                <div className="flex items-center gap-2 justify-center" data-id="element-104">
                  <span className="w-1.5 h-1.5 rounded-full bg-sage" data-id="element-105"></span>
                  <p className="text-lg" data-id="element-106">Noches menos caóticas</p>
                </div>
                <div className="flex items-center gap-2 justify-center" data-id="element-107">
                  <span className="w-1.5 h-1.5 rounded-full bg-sage" data-id="element-108"></span>
                  <p className="text-lg" data-id="element-109">Más sensación de control</p>
                </div>
              </div>

              <p className="text-xl italic font-serif text-charcoal/70" data-id="element-110">
                No perfecto. Pero diferente. Y eso ya cambia todo.
              </p>
            </div>
          </FadeIn>

          <Testimonials data-id="element-111" />
        </div>
      </section>

      {/* 9. URGENCY / WARNING */}
      <section className="py-20 px-6 bg-[#EBE5D9]/60" data-id="element-112">
        <div className="max-w-3xl mx-auto text-center" data-id="element-113">
          <FadeIn data-id="element-114">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-terracotta/10 mb-6" data-id="element-115">
              <AlertCircle className="w-6 h-6 text-terracotta" data-id="element-116" />
            </div>
            <h2 className="text-3xl font-serif mb-8" data-id="element-117">
              Si no haces nada… esto sigue igual
            </h2>

            <div className="flex flex-col sm:flex-row justify-center gap-6 sm:gap-12 mb-10 text-charcoal/80" data-id="element-118">
              <p data-id="element-119">• Las noches interrumpidas</p>
              <p data-id="element-120">• El calor inesperado</p>
              <p data-id="element-121">• La sensación de no entender qué está pasando</p>
            </div>

            <p className="text-xl font-medium mb-4" data-id="element-122">No mejora por sí solo.</p>
            <p className="text-2xl font-serif italic text-terracotta" data-id="element-123">
              Pero cuando entiendes el sistema… puedes empezar a cambiarlo.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section id="pricing" className="py-24 px-6 max-w-3xl mx-auto text-center" data-id="element-124">
        <FadeIn data-id="element-125">
          <h2 className="text-4xl md:text-5xl font-serif mb-6" data-id="element-126">
            Tu cuerpo no está fallando. Está intentando adaptarse.
          </h2>
          <p className="text-xl font-light mb-16 text-charcoal/80" data-id="element-127">
            Y cuando entiendes eso… todo cambia.
          </p>

          <div className="bg-white p-10 md:p-16 rounded-sm shadow-lg border border-sand/50 relative" data-id="element-128">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-terracotta text-white px-6 py-2 rounded-full text-sm font-medium tracking-wide uppercase" data-id="element-129">
              Empieza hoy
            </div>

            <p className="text-lg font-light mb-4 uppercase tracking-widest text-charcoal/60" data-id="element-130">
              Acceso completo por solo:
            </p>
            <div className="text-6xl md:text-7xl font-serif text-charcoal mb-6" data-id="element-131">
              $6.39{' '}
              <span className="text-2xl text-charcoal/50 font-sans font-light" data-id="element-132">
                USD
              </span>
            </div>

            <p className="text-lg text-charcoal/70 mb-10 max-w-md mx-auto" data-id="element-133">
              Menos que una consulta. Pero puede cambiar cómo entiendes tu
              cuerpo para siempre.
            </p>

            <Button onClick={handleCheckout} className="w-full text-xl py-5 mb-8 shadow-md hover:shadow-lg" data-id="element-134">
              👉 Quiero entender lo que me está pasando
            </Button>

            <div className="pt-8 border-t border-charcoal/10" data-id="element-135">
              <p className="text-lg font-medium mb-6" data-id="element-136">
                Hoy tienes dos opciones:
              </p>
              <p className="text-charcoal/80 font-light italic text-lg" data-id="element-137">
                Seguir intentando entenderlo sola… o tener un mapa claro desde
                ahora.
              </p>
            </div>
          </div>

          <div className="mt-20" data-id="element-138">
            <p className="text-xl font-light mb-4" data-id="element-139">
              No necesitas hacerlo todo.
            </p>
            <p className="text-xl font-medium mb-8" data-id="element-140">Solo necesitas empezar.</p>

            <p className="text-2xl font-serif text-terracotta italic mb-8" data-id="element-141">
              Una ancla. Un día. Un pequeño cambio.
            </p>

            <p className="text-lg font-light text-charcoal/80 mb-12" data-id="element-142">
              Y desde ahí… todo empieza a moverse.
            </p>

            <h3 className="text-3xl font-serif" data-id="element-143">
              Tu cuerpo no se equivocó. <br data-id="element-144" />
              <span className="text-sage italic text-2xl mt-2 block" data-id="element-145">
                Solo estaba esperando que lo entendieras.
              </span>
            </h3>
          </div>
        </FadeIn>
      </section>

      {/* 11. FOOTER */}
      <footer className="py-10 border-t border-sand/50" data-id="element-146">
        <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-147">
          <p className="text-charcoal/50 text-sm font-light" data-id="element-148">
            © 2026 Tu cuerpo no se equivocó
          </p>
          <div className="flex items-center gap-6" data-id="element-149">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-charcoal/50 hover:text-terracotta transition-colors font-light" data-id="element-150">
              Privacidad
            </button>
            <span className="text-charcoal/20" data-id="element-151">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-charcoal/50 hover:text-terracotta transition-colors font-light" data-id="element-152">
              Términos
            </button>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-153">
        <PrivacyContent data-id="element-154" />
      </LegalModal>

      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-155">
        <TermsContent data-id="element-156" />
      </LegalModal>
    </div>;
}